// src/tabs/calendar/_beds24-origin.js — v3 ÉTAPE 1 (read-path Beds24)
//
// OUTIL DE DIAGNOSTIC TEMPORAIRE (lecture seule). Objectif : rendre VISIBLE au
// calendrier les blocages qui existent côté Beds24 mais qui ne sont PAS
// représentés dans l'app (ni par un `pricing_block` FBZ, ni par une réservation
// active). Ces divergences sont la matière première de l'ÉTAPE 2 (réconciliation
// / miroir Beds24) — ici on ne fait qu'AFFICHER, on n'écrit rien, on ne pose
// aucun verrou et on ne pousse rien vers Beds24.
//
// Source de vérité Beds24 : `daily_pricing.num_avail` (0 = date bloquée /
// indisponible côté Beds24), déversé par le webhook + le cron pricing-sync
// (toutes les 30 min depuis le 2026-07-03, lecture seule)
// et déjà exposé par `handlePricingCalendarRead` → hook `use-daily-pricing`
// (`pricingMap.get(id).get(date).num_avail`).
//
// Règle de différenciation (TEMPORAIRE, diagnostic) :
//   - `num_avail === 0` couvert par un `pricing_block` FBZ actif  → cohérent
//     (rendu FBZ existant, inchangé).
//   - `num_avail === 0` couvert par une réservation active        → cohérent
//     (le séjour bloque la dispo Beds24, rendu résa existant, inchangé).
//   - `num_avail === 0` SANS block FBZ ni résa active             → « Bloqué sur
//     Beds24 » : divergence rendue VISUELLEMENT DISTINCTE (hachure ciel).
//
// Fonction PURE et testable : ne dépend que des données déjà chargées.

import { findBlockForDate } from "./_block-helpers"

// Visuel « Bloqué sur Beds24 » — charte marine, hachure ciel diagonale + bordure
// marine tiretée. VOLONTAIREMENT distinct du bloc FBZ (gris plein `srcBloque`),
// des résas (couleurs de source pleines) et des barres arrondies pleines : la
// trame hachurée signale au premier coup d'œil « source externe, non représenté
// dans l'app » sans jargon technique. Différenciation TEMPORAIRE (diagnostic).
export const BEDS24_ORIGIN_VISUAL = {
  // Hachure diagonale ciel (deux tons doux du bleu logo).
  background:
    "repeating-linear-gradient(45deg, #dfecf9, #dfecf9 5px, #a9cdee 5px, #a9cdee 10px)",
  border: "1px dashed #215387", // marine tireté
  textColor: "#1b3a5c", // marineDark
  label: "Bloqué sur Beds24",
}

// Micro-UX M14 (feedback post-réouverture) — visuel « Réouverture envoyée ».
// Après un resolve `rouvrir` réussi (push Beds24 OK ou mis en file), le miroir
// `daily_pricing.num_avail` reste stale (num_avail=0) jusqu'au prochain
// webhook/cron : le segment hachuré persisterait et l'admin doute (recharge en
// boucle). État OPTIMISTE purement visuel : le segment traité passe en gris
// neutre non cliquable « Réouverture envoyée ✓ » jusqu'au refresh du miroir.
// AUCUNE écriture, aucun impact sur le push lui-même (CHEMIN ARGENT intouché).
export const BEDS24_REOPENED_VISUAL = {
  background: "#f0f2f5", // gris neutre plein (la hachure disparaît)
  border: "1px dashed #9295a0", // gris tireté — état transitoire
  textColor: "#5f6470",
  label: "Réouverture envoyée ✓",
}

/**
 * Vrai si un span « Bloqué sur Beds24 » du calendrier chevauche un segment
 * dont la réouverture vient d'être envoyée (état optimiste session).
 * Toutes les dates sont ISO YYYY-MM-DD, bornes INCLUSES des deux côtés
 * (comparaison lexicographique = chronologique).
 *
 * @param {{logementId?:string, startISO?:string, endISO?:string}} span
 * @param {Array<{logementId:string, start:string, end:string}>|null} reopenedSegments
 * @returns {boolean}
 */
export function isSpanReopened(span, reopenedSegments) {
  if (!Array.isArray(reopenedSegments) || reopenedSegments.length === 0) return false
  const { logementId, startISO, endISO } = span || {}
  if (!logementId || !startISO || !endISO) return false
  return reopenedSegments.some(
    (seg) =>
      seg &&
      seg.logementId === logementId &&
      typeof seg.start === "string" &&
      typeof seg.end === "string" &&
      startISO <= seg.end &&
      endISO >= seg.start,
  )
}

// Itère les dates ISO de start à end INCLUSES (cap défensif 1000 jours).
// Dates malformées → tableau vide (le segment sera traité comme injugeable).
function isoRangeInclusive(startISO, endISO) {
  const a = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startISO || "")
  const b = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endISO || "")
  if (!a || !b) return []
  const out = []
  const cur = new Date(Date.UTC(+a[1], +a[2] - 1, +a[3]))
  const stop = Date.UTC(+b[1], +b[2] - 1, +b[3])
  while (cur.getTime() <= stop && out.length < 1000) {
    out.push(cur.toISOString().slice(0, 10))
    cur.setUTCDate(cur.getUTCDate() + 1)
  }
  return out
}

/**
 * MINEUR-1 revue #993 — purge des segments optimistes CONFIRMÉS par le miroir.
 * L'état « Réouverture envoyée ✓ » ne doit couvrir que la fenêtre STALE
 * (num_avail encore à 0 en base alors que le push est parti) : dès que le
 * miroir rafraîchi montre les dates rouvertes (num_avail ≠ 0), l'entrée
 * optimiste est obsolète et doit disparaître — sinon un NOUVEAU blocage
 * Beds24 légitime sur les mêmes dates serait grisé « envoyée ✓ » à tort.
 *
 * Règle par segment :
 *   - miroir du logement absent, ou AUCUNE date du segment chargée dans le
 *     miroir (ex. mois non affiché) → injugeable, on GARDE ;
 *   - ≥ 1 date du segment encore num_avail=0 → fenêtre stale, on GARDE ;
 *   - ≥ 1 date chargée et AUCUNE bloquée → miroir a confirmé, on PURGE.
 * Segments malformés → purgés (défensif).
 *
 * Retourne la MÊME référence si rien n'est purgé (setState no-op côté React).
 *
 * @param {Array<{logementId:string, start:string, end:string}>|null} reopenedSegments
 * @param {Map<string, Map<string, {num_avail?:number}>>|null} pricingMap
 * @returns {Array} segments encore pertinents
 */
export function pruneReopenedSegments(reopenedSegments, pricingMap) {
  if (!Array.isArray(reopenedSegments) || reopenedSegments.length === 0) {
    return Array.isArray(reopenedSegments) ? reopenedSegments : []
  }
  const kept = reopenedSegments.filter((seg) => {
    if (!seg || !seg.logementId || !seg.start || !seg.end) return false
    const dates = isoRangeInclusive(seg.start, seg.end)
    if (dates.length === 0) return false // dates malformées → purge défensive
    const forLog = pricingMap?.get?.(seg.logementId)
    if (!forLog) return true // miroir non chargé → injugeable, on garde
    let seen = 0
    for (const iso of dates) {
      const cell = forLog.get(iso)
      if (!cell) continue
      seen++
      if (cell.num_avail === 0) return true // encore stale → on garde
    }
    return seen === 0 // aucune date visible → injugeable ; sinon confirmé → purge
  })
  return kept.length === reopenedSegments.length ? reopenedSegments : kept
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

/**
 * Calcule les segments (barres continues) des dates bloquées côté Beds24
 * (`num_avail === 0`) qui NE SONT PAS déjà représentées dans l'app (ni block
 * FBZ actif, ni réservation active).
 *
 * @param {object} args
 * @param {Map<string, {num_avail?:number}>|null|undefined} args.pricingForLogement
 *   — Map dateISO → cellule pricing (contient `num_avail`), telle que fournie par
 *   `useDailyPricing` (`pricingMap.get(logementId)`).
 * @param {Array<object>|null} args.blocksForLogement — pricing_blocks FBZ actifs
 *   du logement (`blocksByLogement.get(logementId)`).
 * @param {Map<string, string>|null} args.resaIdByDate — Map dateISO → resaId des
 *   réservations ACTIVES couvrant la date (déjà dédup / hors annulées côté
 *   TableauView `resaIdByLogementByDate.get(logementId)`).
 * @param {Array<Date>} args.monthDays — jours du mois affiché (1er → dernier),
 *   identique au `monthDays` de TableauView.
 * @returns {Array<{ startCol:number, endCol:number }>} spans 0-based (contigus)
 *   des dates « Bloqué sur Beds24 » non représentées ; [] si aucune.
 */
export function beds24BlockedSpans({
  pricingForLogement,
  blocksForLogement,
  resaIdByDate,
  monthDays,
} = {}) {
  if (!pricingForLogement || !monthDays || monthDays.length === 0) return []

  const N = monthDays.length
  const out = []
  let runStart = -1

  const flush = (endExclusive) => {
    if (runStart >= 0) {
      out.push({ startCol: runStart, endCol: endExclusive - 1 })
      runStart = -1
    }
  }

  for (let i = 0; i < N; i++) {
    const dateISO = toISODate(monthDays[i])
    const cell = pricingForLogement.get(dateISO)
    const blockedOnBeds24 = cell?.num_avail === 0
    // Couvert dans l'app → cohérent, jamais un « Beds24-origin ».
    const coveredByFbz = !!findBlockForDate(blocksForLogement, dateISO)
    const coveredByResa = !!(resaIdByDate && resaIdByDate.get(dateISO))
    const isBeds24Only = blockedOnBeds24 && !coveredByFbz && !coveredByResa

    if (isBeds24Only) {
      if (runStart < 0) runStart = i
    } else {
      flush(i)
    }
  }
  flush(N)
  return out
}
